// Complete CampMap.jsx - Fixed navigation and state preservation with auto-location
import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {MAPBOX_ACCESS_TOKEN} from './mapboxConfig.js';

import { Tent } from 'lucide-react';


import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Icon SVG strings for Mapbox
const CAMPGROUND_ICON_SVG = `
<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="11" fill="white" stroke="#4CAF50" stroke-width="2"/>
  <path d="M6 18L12 6L18 18H6Z" fill="#4CAF50" stroke="white" stroke-width="1.5" stroke-linejoin="round"/>
  <line x1="12" y1="6" x2="12" y2="18" stroke="white" stroke-width="1.5"/>
  <path d="M10 18L12 14L14 18" stroke="white" stroke-width="1.5" fill="none"/>
</svg>`;

const DISPERSED_ICON_SVG = `
<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="11" fill="white" stroke="#FF9800" stroke-width="2"/>
  <path d="M8 18V16L6 12L8 8L10 12L8 16" fill="#FF9800"/>
  <rect x="7.5" y="16" width="1" height="2" fill="#FF9800"/>
  <path d="M16 18V15L14 11L16 7L18 11L16 15" fill="#FF9800"/>
  <rect x="15.5" y="15" width="1" height="3" fill="#FF9800"/>
  <path d="M11 18L12 14L13 18" fill="#FF9800" opacity="0.7"/>
</svg>`;

const RV_PARK_ICON_SVG = `
<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="11" fill="white" stroke="#2196F3" stroke-width="2"/>
  <rect x="6" y="10" width="12" height="6" rx="1" fill="#2196F3"/>
  <rect x="14" y="11" width="3" height="2" rx="0.5" fill="white"/>
  <rect x="8" y="12" width="2" height="3" rx="0.3" fill="white"/>
  <circle cx="8" cy="17" r="1.5" fill="#2196F3"/>
  <circle cx="16" cy="17" r="1.5" fill="#2196F3"/>
  <circle cx="8" cy="17" r="0.8" fill="white"/>
  <circle cx="16" cy="17" r="0.8" fill="white"/>
  <rect x="5" y="12.5" width="1" height="1" fill="#2196F3"/>
</svg>`;

const GROUP_CAMP_ICON_SVG = `
<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="11" fill="white" stroke="#9C27B0" stroke-width="2"/>
  <path d="M5 18L10 8L15 18H5Z" fill="#9C27B0" stroke="white" stroke-width="1" stroke-linejoin="round"/>
  <path d="M15 18L18 12L21 18H15Z" fill="#9C27B0" stroke="white" stroke-width="1" stroke-linejoin="round" opacity="0.8"/>
  <line x1="10" y1="8" x2="10" y2="18" stroke="white" stroke-width="1"/>
  <line x1="18" y1="12" x2="18" y2="18" stroke="white" stroke-width="1"/>
  <circle cx="7" cy="16" r="0.8" fill="white"/>
  <circle cx="10" cy="16" r="0.8" fill="white"/>
  <circle cx="13" cy="16" r="0.8" fill="white"/>
</svg>`;

const STEALTH_CAMP_ICON_SVG = `
<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="11" fill="white" stroke="#424242" stroke-width="2"/>
  <path d="M7 18L12 7L17 18H7Z" fill="#424242" stroke="white" stroke-width="1.5" stroke-linejoin="round"/>
  <ellipse cx="10" cy="12" rx="1.5" ry="1" fill="white" opacity="0.4"/>
  <ellipse cx="14" cy="14" rx="1" ry="1.5" fill="white" opacity="0.4"/>
  <circle cx="12" cy="10" r="0.8" fill="white" opacity="0.3"/>
  <line x1="12" y1="7" x2="12" y2="18" stroke="white" stroke-width="1.5"/>
  <path d="M11 18L12 15L13 18" stroke="white" stroke-width="1" fill="none" opacity="0.7"/>
  <path d="M6 18C6 16 7 15 7 15C7 15 8 16 8 18" fill="#424242" opacity="0.6"/>
  <path d="M16 18C16 16 17 15 17 15C17 15 18 16 18 18" fill="#424242" opacity="0.6"/>
</svg>`;

const NO_CAMPING_ICON_SVG = `
<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="11" fill="white" stroke="#F44336" stroke-width="2"/>
  <path d="M6 18L12 6L18 18H6Z" fill="#999" stroke="white" stroke-width="1.5" stroke-linejoin="round" opacity="0.6"/>
  <line x1="12" y1="6" x2="12" y2="18" stroke="white" stroke-width="1.5" opacity="0.6"/>
  <circle cx="12" cy="12" r="9" fill="none" stroke="#F44336" stroke-width="2.5"/>
  <line x1="6" y1="6" x2="18" y2="18" stroke="#F44336" stroke-width="3" stroke-linecap="round"/>
</svg>`;

// Enhanced global function to handle camp details navigation with map state
if (typeof window !== 'undefined') {
    window.navigateToCampDetails = (campLocationId, userId) => {
        if (!campLocationId || !userId) {
            alert('Unable to navigate: missing camp or user information');
            return;
        }

        const currentMapState = window.getCurrentMapState ? window.getCurrentMapState() : null;
        const baseUrl = `/events/otgcampdetails/${campLocationId}`;
        const params = new URLSearchParams();

        if (currentMapState) {
            // Store with high precision
            params.set('returnLat', currentMapState.center.lat.toFixed(8));
            params.set('returnLng', currentMapState.center.lng.toFixed(8));
            params.set('returnZoom', currentMapState.zoom.toFixed(6));
            params.set('returnFrom', 'map');

            console.log('🗺️ Storing map state for navigation:', {
                lat: currentMapState.center.lat,
                lng: currentMapState.center.lng,
                zoom: currentMapState.zoom
            });
        }

        const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
        console.log('🔗 Navigating to camp details:', url);

        if (window.reactRouterNavigate) {
            window.reactRouterNavigate(url);
        } else {
            window.location.href = url;
        }
    };
}

const CampMap = ({
                     mapboxToken = MAPBOX_ACCESS_TOKEN,
                     apiEndpoint = '/otgservices/getMapCampingData.php',
                     initialCenter = [-122.98, 39.7],
                     initialZoom = 10
                 }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Refs for stable access
    const mapContainer = useRef(null);
    const map = useRef(null);
    const popup = useRef(null);
    const userLocationMarker = useRef(null);
    const selectedLocationMarker = useRef(null);
    const mapEventHandlersRef = useRef({});
    const loadingTimeoutRef = useRef(null);
    const isInitializedRef = useRef(false);
    const isUpdatingMapRef = useRef(false);
    const styleDataHandlerRef = useRef(null);
    const isChangingStyleRef = useRef(false);

    // Data and function refs
    const currentCampDataRef = useRef(null);
    const currentUserIdRef = useRef(null);
    const currentFiltersRef = useRef({ type: 'all', route: 'all', status: 'all' });
    const dataLoadedRef = useRef(false);

    // Store stable references to props and functions to avoid dependency issues
    const propsRef = useRef({ mapboxToken, apiEndpoint, initialCenter, initialZoom });
    const functionsRef = useRef({});

    // State
    const [legendExpanded, setLegendExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedFilters, setSelectedFilters] = useState({
        type: 'all',
        route: 'all',
        status: 'all'
    });
    const [campData, setCampData] = useState(null);
    const [mapStyle, setMapStyle] = useState('outdoors');
    const [userLocation, setUserLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [scaleWidth, setScaleWidth] = useState(100);
    const [scaleMiles, setScaleMiles] = useState({value: 1, unit: 'mi'});
    const [nearMe, setNearMe] = useState(false);

    const [ isAdmin, setAdmin ] = useState(false);
    const [ isTA, setTA] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);

    // Keep refs in sync
    useEffect(() => {
        currentFiltersRef.current = selectedFilters;
        propsRef.current = { mapboxToken, apiEndpoint, initialCenter, initialZoom };
    }, [selectedFilters, mapboxToken, apiEndpoint, initialCenter, initialZoom]);

    // Map style options
    const mapStyles = useMemo(() => ({
        streets: {
            style: 'mapbox://styles/mapbox/streets-v12',
            name: '🗺️ Streets',
            description: 'Road map with street details'
        },
        outdoors: {
            style: 'mapbox://styles/mapbox/outdoors-v12',
            name: '🏔️ Terrain',
            description: 'Topographic map with elevation'
        },
        satellite: {
            style: 'mapbox://styles/mapbox/satellite-v9',
            name: '🛰️ Satellite',
            description: 'Aerial satellite imagery'
        },
        hybrid: {
            style: 'mapbox://styles/mapbox/satellite-streets-v12',
            name: '🗺️🛰️ Hybrid',
            description: 'Satellite with street overlay'
        }
    }), []);

    // FIXED: Stable loading function with timeout
    const setLoadingWithTimeout = useCallback((loading) => {
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
        }

        setIsLoading(loading);

        if (loading) {
            loadingTimeoutRef.current = setTimeout(() => {
                console.warn('Loading timeout reached, forcing loading to stop');
                setIsLoading(false);
                if (!dataLoadedRef.current) {
                    setError('Loading timeout - please try refreshing the page');
                }
            }, 10000);
        }
    }, []);

    // Enhanced getCurrentMapState with debugging
    const getCurrentMapState = useCallback(() => {
        if (!map.current || !map.current.loaded()) {
            console.log('❌ Map not ready for state capture');
            return null;
        }

        const center = map.current.getCenter();
        const zoom = map.current.getZoom();

        const state = {
            center: { lat: center.lat, lng: center.lng },
            zoom: zoom
        };

        console.log('📍 Current map state captured:', state);
        return state;
    }, []);

    const updateScale = useCallback(() => {
        if (!map.current || !map.current.loaded()) return;

        try {
            const center = map.current.getCenter();
            const point1 = map.current.project(center);
            const point2 = { x: point1.x + 100, y: point1.y };
            const coord1 = map.current.unproject(point1);
            const coord2 = map.current.unproject(point2);

            const lon1 = coord1.lng * Math.PI / 180;
            const lon2 = coord2.lng * Math.PI / 180;
            const lat1 = coord1.lat * Math.PI / 180;
            const lat2 = coord2.lat * Math.PI / 180;

            const R = 3958.8;
            const x = (lon2 - lon1) * Math.cos((lat1 + lat2) / 2);
            const y = (lat2 - lat1);
            const d = Math.sqrt(x * x + y * y) * R;

            let cleanDistance, unit = 'mi';
            if (d >= 100) {
                cleanDistance = Math.round(d / 50) * 50;
            } else if (d >= 10) {
                cleanDistance = Math.round(d / 10) * 10;
            } else if (d >= 1) {
                cleanDistance = Math.round(d);
            } else {
                const feet = d * 5280;
                unit = 'ft';
                if (feet >= 1000) {
                    cleanDistance = Math.round(feet / 1000) * 1000;
                } else if (feet >= 100) {
                    cleanDistance = Math.round(feet / 100) * 100;
                } else {
                    cleanDistance = Math.round(feet / 10) * 10;
                    if (cleanDistance < 10) cleanDistance = 10;
                }
            }

            const newWidth = unit === 'mi' ? (cleanDistance / d) * 100 : (cleanDistance / d / 5280) * 100;

            setScaleWidth(newWidth);
            setScaleMiles({value: cleanDistance, unit: unit});
        } catch (error) {
            console.error('Scale calculation error:', error);
        }
    }, []);

    const addUserLocationToMap = useCallback((location) => {
        if (!map.current || !map.current.loaded()) return;

        if (!location.latitude || !location.longitude ||
            isNaN(location.latitude) || isNaN(location.longitude) ||
            Math.abs(location.latitude) > 90 || Math.abs(location.longitude) > 180) {
            setLocationError('Invalid coordinates received');
            return;
        }

        if (userLocationMarker.current) {
            userLocationMarker.current.remove();
            userLocationMarker.current = null;
        }

        const lngLat = [parseFloat(location.longitude), parseFloat(location.latitude)];

        const markerEl = document.createElement('div');
        markerEl.className = 'user-location-marker';

        if (!document.getElementById('user-location-styles')) {
            const style = document.createElement('style');
            style.id = 'user-location-styles';
            style.textContent = `
                .user-location-marker {
                    width: 24px !important;
                    height: 24px !important;
                    border-radius: 50% !important;
                    background-color: #007AFF !important;
                    border: 4px solid white !important;
                    box-shadow: 0 0 15px rgba(0,122,255,0.6) !important;
                    cursor: pointer !important;
                    animation: userLocationPulse 2s infinite !important;
                    box-sizing: border-box !important;
                    position: relative !important;
                }
                @keyframes userLocationPulse {
                    0% { transform: scale(1) !important; opacity: 1 !important; }
                    50% { transform: scale(1.3) !important; opacity: 0.6 !important; }
                    100% { transform: scale(1) !important; opacity: 1 !important; }
                }
            `;
            document.head.appendChild(style);
        }

        userLocationMarker.current = new mapboxgl.Marker({ element: markerEl })
            .setLngLat(lngLat)
            .addTo(map.current);

        const locationPopup = new mapboxgl.Popup({ offset: 30, closeButton: true, closeOnClick: false })
            .setHTML(`
                <div style="padding: 12px; min-width: 200px;">
                    <h3 style="margin: 0 0 8px 0; color: #007AFF; font-size: 16px;">📍 Your Location</h3>
                    <div style="margin-bottom: 6px;">
                        <strong>Latitude:</strong> ${location.latitude.toFixed(6)}
                    </div>
                    <div style="margin-bottom: 6px;">
                        <strong>Longitude:</strong> ${location.longitude.toFixed(6)}
                    </div>
                    ${location.accuracy ? `
                        <div style="margin-bottom: 6px;">
                            <strong>Accuracy:</strong> ±${Math.round(location.accuracy)}m
                        </div>
                    ` : ''}
                    <div style="font-size: 11px; color: #666; margin-top: 8px;">
                        Click to close this popup
                    </div>
                </div>
            `);
        userLocationMarker.current.setPopup(locationPopup);
    }, []);

    const getCurrentLocation = useCallback(() => {
        setIsGettingLocation(true);
        setLocationError(null);

        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by this browser');
            setIsGettingLocation(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude, accuracy } = position.coords;

                if (!latitude || !longitude ||
                    latitude === 0 || longitude === 0 ||
                    isNaN(latitude) || isNaN(longitude) ||
                    Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
                    setLocationError(`Invalid coordinates: ${latitude}, ${longitude}`);
                    setIsGettingLocation(false);
                    return;
                }

                const location = { latitude, longitude, accuracy };
                setUserLocation(location);
                setIsGettingLocation(false);

                if (map.current && map.current.loaded()) {
                    addUserLocationToMap(location);
                    map.current.flyTo({
                        center: [longitude, latitude],
                        zoom: 8,
                        duration: 2000
                    });
                }
            },
            (error) => {
                let errorMessage;
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = "Location access denied by user";
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = "Location information unavailable";
                        break;
                    case error.TIMEOUT:
                        errorMessage = "Location request timed out";
                        break;
                    default:
                        errorMessage = `Unknown error occurred (${error.code})`;
                        break;
                }
                setLocationError(errorMessage);
                setIsGettingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 60000
            }
        );
    }, [addUserLocationToMap]);

    const addSelectedLocationToMap = useCallback((location) => {
        if (!map.current || !map.current.loaded()) return;

        // Remove existing selected location marker
        if (selectedLocationMarker.current) {
            selectedLocationMarker.current.remove();
            selectedLocationMarker.current = null;
        }

        const lngLat = [parseFloat(location.lng), parseFloat(location.lat)];

        const markerEl = document.createElement('div');
        markerEl.className = 'selected-location-marker';

        if (!document.getElementById('selected-location-styles')) {
            const style = document.createElement('style');
            style.id = 'selected-location-styles';
            style.textContent = `
                .selected-location-marker {
                    width: 20px !important;
                    height: 20px !important;
                    border-radius: 50% !important;
                    background-color: #FF6B35 !important;
                    border: 3px solid white !important;
                    box-shadow: 0 0 12px rgba(255,107,53,0.8) !important;
                    cursor: pointer !important;
                    animation: selectedLocationPulse 1.5s infinite !important;
                    box-sizing: border-box !important;
                    position: relative !important;
                }
                @keyframes selectedLocationPulse {
                    0% { transform: scale(1) !important; opacity: 1 !important; }
                    50% { transform: scale(1.2) !important; opacity: 0.7 !important; }
                    100% { transform: scale(1) !important; opacity: 1 !important; }
                }
            `;
            document.head.appendChild(style);
        }

        selectedLocationMarker.current = new mapboxgl.Marker({ element: markerEl })
            .setLngLat(lngLat)
            .addTo(map.current);

        const locationPopup = new mapboxgl.Popup({ offset: 25, closeButton: true, closeOnClick: false })
            .setHTML(`
                <div style="padding: 10px; min-width: 180px;">
                    <h3 style="margin: 0 0 8px 0; color: #FF6B35; font-size: 14px;">📍 Selected Location</h3>
                    <div style="margin-bottom: 4px; font-size: 12px;">
                        <strong>Latitude:</strong> ${location.lat.toFixed(6)}
                    </div>
                    <div style="margin-bottom: 8px; font-size: 12px;">
                        <strong>Longitude:</strong> ${location.lng.toFixed(6)}
                    </div>
                    <div style="font-size: 10px; color: #666; margin-top: 6px;">
                        Click "Add Location" to use this position
                    </div>
                </div>
            `);
        selectedLocationMarker.current.setPopup(locationPopup);
    }, []);

    const handleAddLocation = useCallback(() => {
        // Check if a location is selected
        if (!selectedLocation) {
            alert('Please click on a location in the map first to select where you want to add the new location.');
            return;
        }

        const currentMapState = getCurrentMapState();
        const baseUrl = '/events/campmap/addLocation';
        const params = new URLSearchParams();

        if (currentMapState) {
            // Store current map state for return navigation
            params.set('returnLat', currentMapState.center.lat.toFixed(8));
            params.set('returnLng', currentMapState.center.lng.toFixed(8));
            params.set('returnZoom', currentMapState.zoom.toFixed(6));
            params.set('returnFrom', 'map');

            // Use selected location (we already checked it exists)
            params.set('selectedLat', selectedLocation.lat.toFixed(8));
            params.set('selectedLng', selectedLocation.lng.toFixed(8));

            console.log('🗺️ Storing map state for add location navigation:', {
                lat: currentMapState.center.lat,
                lng: currentMapState.center.lng,
                zoom: currentMapState.zoom,
                selectedLocation: selectedLocation
            });
        }

        const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
        console.log('🔗 Navigating to add location:', url);
        navigate(url);
    }, [getCurrentMapState, navigate, selectedLocation]);


    // FIXED: Icon loading function
    const loadIcons = useCallback(async () => {
        if (!map.current || !map.current.loaded()) {
            return false;
        }

        console.log('Loading/reloading icons...');

        const iconData = [
            { name: 'campground-icon', svg: CAMPGROUND_ICON_SVG },
            { name: 'dispersed-icon', svg: DISPERSED_ICON_SVG },
            { name: 'rv-park-icon', svg: RV_PARK_ICON_SVG },
            { name: 'group-camp-icon', svg: GROUP_CAMP_ICON_SVG },
            { name: 'stealth-camp-icon', svg: STEALTH_CAMP_ICON_SVG },
            { name: 'no-camping-icon', svg: NO_CAMPING_ICON_SVG }
        ];

        try {
            for (const icon of iconData) {
                // Always remove existing icon first when reloading
                if (map.current.hasImage(icon.name)) {
                    map.current.removeImage(icon.name);
                    console.log(`Removed existing icon: ${icon.name}`);
                }

                const img = new Image(32, 32);
                const dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(icon.svg);

                await new Promise((resolve, reject) => {
                    img.onload = () => {
                        try {
                            map.current.addImage(icon.name, img);
                            console.log(`✓ Loaded icon: ${icon.name}`);
                            resolve();
                        } catch (error) {
                            console.error(`✗ Error adding icon to map: ${icon.name}`, error);
                            reject(error);
                        }
                    };
                    img.onerror = () => {
                        console.error(`✗ Error loading icon image: ${icon.name}`);
                        reject(new Error(`Failed to load ${icon.name}`));
                    };
                    img.src = dataUrl;
                });
            }
            console.log('✓ All icons loaded successfully');
            return true;
        } catch (error) {
            console.error('✗ Error loading icons:', error);
            return false;
        }
    }, []);

    // FIXED: Data loading function - now stable
    const loadCampData = useCallback(async () => {
        if (isUpdatingMapRef.current) return;

        console.log('Loading camp data...');
        setLoadingWithTimeout(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            const filters = currentFiltersRef.current;
            const { apiEndpoint: endpoint } = propsRef.current;

            if (filters.type && filters.type !== 'all') {
                params.append('type', filters.type);
            }
            if (filters.route && filters.route !== 'all') {
                params.append('route', filters.route);
            }
            if (filters.status && filters.status !== 'all') {
                params.append('status', filters.status);
            }

            // Add location parameters when Near Me is active
            if (nearMe && userLocation && userLocation.latitude && userLocation.longitude) {
                params.append('latitude', userLocation.latitude.toString());
                params.append('longitude', userLocation.longitude.toString());
                console.log('Adding Near Me coordinates to API request:', {
                    latitude: userLocation.latitude,
                    longitude: userLocation.longitude
                });
            }

            const url = params.toString() ? `${endpoint}?${params}` : endpoint;

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);

            const response = await fetch(url, {
                signal: controller.signal,
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(data.message || 'Server returned an error');
            }

            currentUserIdRef.current = data.userId || null;

            const geoJSON = {
                type: data.type || 'FeatureCollection',
                features: data.features || []
            };

            setAdmin(data.isAdmin);
            setTA(data.trailAmbassador);

            setCampData(geoJSON);
            currentCampDataRef.current = geoJSON;
            dataLoadedRef.current = true;

            // Update map if ready - use function from ref
            if (map.current && map.current.loaded() && functionsRef.current.updateMapData) {
                await functionsRef.current.updateMapData(geoJSON, currentUserIdRef.current);
            }

            setLoadingWithTimeout(false);

        } catch (err) {
            console.error('Error loading camp data:', err);

            if (err.name === 'AbortError') {
                setError('Request timed out - please check your connection and try again');
            } else {
                setError(`Failed to load data: ${err.message}`);
            }

            setLoadingWithTimeout(false);
        }
    }, [setLoadingWithTimeout, nearMe, userLocation]);

    // FIXED: Map data update function with proper icon reloading
    const updateMapData = useCallback(async (geoJSON, userId = null) => {
        if (!map.current || !geoJSON || !map.current.loaded() || isUpdatingMapRef.current) {
            return;
        }

        console.log('Updating map data with', geoJSON.features?.length || 0, 'features');
        isUpdatingMapRef.current = true;

        try {
            // Clean up existing layers and sources
            const layersToRemove = ['camp-labels', 'camp-points', 'camp-cluster-count', 'camp-clusters'];
            layersToRemove.forEach(layer => {
                if (map.current.getLayer(layer)) {
                    map.current.removeLayer(layer);
                }
            });

            if (map.current.getSource('camp-locations')) {
                map.current.removeSource('camp-locations');
            }

            // Clean up event handlers
            Object.entries(mapEventHandlersRef.current).forEach(([event, handler]) => {
                const [eventType, layerId] = event.split('-');
                if (layerId) {
                    map.current.off(eventType, layerId, handler);
                }
            });
            mapEventHandlersRef.current = {};

            // CRITICAL: Always reload icons to ensure they're available after style changes
            console.log('Reloading icons for current map style...');
            await loadIcons();

            // Add data source
            map.current.addSource('camp-locations', {
                type: 'geojson',
                data: geoJSON,
                cluster: true,
                clusterMaxZoom: 14,
                clusterRadius: 30
            });

            // Add cluster layers
            map.current.addLayer({
                id: 'camp-clusters',
                type: 'circle',
                source: 'camp-locations',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': [
                        'step',
                        ['get', 'point_count'],
                        '#51bbd6',
                        5, '#f1f075',
                        10, '#f28cb1'
                    ],
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        20, 5, 30, 10, 40
                    ]
                }
            });

            map.current.addLayer({
                id: 'camp-cluster-count',
                type: 'symbol',
                source: 'camp-locations',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12
                }
            });

            // Add individual camp points with icons
            map.current.addLayer({
                id: 'camp-points',
                type: 'symbol',
                source: 'camp-locations',
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'icon-image': [
                        'match',
                        ['get', 'type'],
                        'campground', 'campground-icon',
                        'dispersed', 'dispersed-icon',
                        'rv park', 'rv-park-icon',
                        'group camp', 'group-camp-icon',
                        'stealth camp', 'stealth-camp-icon',
                        'no camping', 'no-camping-icon',
                        'campground-icon' // fallback
                    ],
                    'icon-size': 1,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true
                }
            });

            // Verify icons are loaded
            console.log('Verifying icon availability:');
            ['campground-icon', 'dispersed-icon', 'rv-park-icon', 'group-camp-icon', 'stealth-camp-icon', 'no-camping-icon'].forEach(iconName => {
                const hasIcon = map.current.hasImage(iconName);
                console.log(`${iconName}: ${hasIcon ? '✓' : '✗'}`);
            });

            // Add labels
            map.current.addLayer({
                id: 'camp-labels',
                type: 'symbol',
                source: 'camp-locations',
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'text-field': ['get', 'name'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-offset': [0, 2],
                    'text-anchor': 'top',
                    'text-size': 12
                },
                paint: {
                    'text-color': '#333',
                    'text-halo-color': '#fff',
                    'text-halo-width': 1
                }
            });

            // Add event handlers
            const clusterClickHandler = (e) => {
                const features = map.current.queryRenderedFeatures(e.point, {
                    layers: ['camp-clusters']
                });
                const clusterId = features[0].properties.cluster_id;
                map.current.getSource('camp-locations').getClusterExpansionZoom(
                    clusterId,
                    (err, zoom) => {
                        if (err) return;
                        map.current.easeTo({
                            center: features[0].geometry.coordinates,
                            zoom: zoom
                        });
                    }
                );
            };

            const pointsClickHandler = (e) => {
                e.preventDefault();
                e.originalEvent.stopPropagation();

                const coordinates = e.features[0].geometry.coordinates.slice();
                const props = e.features[0].properties;
                const longitude = coordinates[0];
                const latitude = coordinates[1];
                const campLocationId = props.camp_location_id || props.id || props.otg_camp_location;

                popup.current
                    .setLngLat(coordinates)
                    .setHTML(`
                        <div style="padding: 15px; max-width: 320px;">
                            <h3 style="margin: 0 0 12px 0; color: #333; font-size: 18px; border-bottom: 2px solid #eee; padding-bottom: 8px;">
                                ${props.name}
                            </h3>
                            
                            <div style="margin-bottom: 8px;">
                                <strong>Type:</strong> 
                                <span style="background: ${
                        props.type === 'campground' ? '#4CAF50' :
                            props.type === 'dispersed' ? '#FF9800' :
                                props.type === 'rv_park' ? '#2196F3' :
                                    props.type === 'group camp' ? '#9C27B0' :
                                        props.type === 'stealth camp' ? '#424242' :
                                            props.type === 'no camping' ? '#F44336' : '#757575'
                    }; color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; text-transform: capitalize;">
                                    ${props.type}
                                </span>
                            </div>
                            
                            ${props.description && props.description.trim() && props.description !== 'null' ? `
                                <div style="margin-bottom: 12px; padding: 10px; background: #f8f9fa; border-left: 4px solid #2563eb; border-radius: 4px;">
                                    <strong style="color: #1e40af; display: block; margin-bottom: 6px;">Description:</strong>
                                    <p style="margin: 0; color: #374151; line-height: 1.4; font-style: italic;">
                                        ${props.description}
                                    </p>
                                </div>
                            ` : `
                                <div style="margin-bottom: 12px; padding: 8px; background: #f3f4f6; border-radius: 4px; text-align: center;">
                                    <span style="color: #6b7280; font-style: italic; font-size: 14px;">No description available</span>
                                </div>
                            `}
                            
                            ${(userId !== null && userId !== undefined) ? `
                                <div style="margin-bottom: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
                                    <button 
                                        onclick="window.navigateToCampDetails('${campLocationId}', '${userId}')" 
                                        style="
                                            width: 100%; 
                                            background: #2563eb; 
                                            color: white; 
                                            border: none; 
                                            padding: 10px 16px; 
                                            border-radius: 6px; 
                                            font-size: 14px; 
                                            font-weight: 500;
                                            cursor: pointer; 
                                            transition: all 0.2s;
                                            display: flex;
                                            align-items: center;
                                            justify-content: center;
                                            gap: 8px;
                                        "
                                        onmouseover="this.style.background='#1d4ed8'"
                                        onmouseout="this.style.background='#2563eb'"
                                        title="View and edit camp details"
                                    >
                                        <span style="font-size: 16px;">🏕️</span>
                                        Camp Details
                                    </button>
                                </div>
                            ` : ''}
                            
                            <div style="font-size: 11px; color: #9ca3af; margin-top: 10px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                                📍 ${latitude.toFixed(6)}, ${longitude.toFixed(6)}
                            </div>
                        </div>
                    `)
                    .addTo(map.current);
            };

            const mouseEnterHandler = () => {
                map.current.getCanvas().style.cursor = 'pointer';
            };
            const mouseLeaveHandler = () => {
                map.current.getCanvas().style.cursor = '';
            };

            // Add event listeners
            map.current.on('click', 'camp-clusters', clusterClickHandler);
            map.current.on('click', 'camp-points', pointsClickHandler);
            map.current.on('mouseenter', 'camp-clusters', mouseEnterHandler);
            map.current.on('mouseleave', 'camp-clusters', mouseLeaveHandler);
            map.current.on('mouseenter', 'camp-points', mouseEnterHandler);
            map.current.on('mouseleave', 'camp-points', mouseLeaveHandler);

            // Store handlers for cleanup
            mapEventHandlersRef.current = {
                'click-camp-clusters': clusterClickHandler,
                'click-camp-points': pointsClickHandler,
                'mouseenter-camp-clusters': mouseEnterHandler,
                'mouseleave-camp-clusters': mouseLeaveHandler,
                'mouseenter-camp-points': mouseEnterHandler,
                'mouseleave-camp-points': mouseLeaveHandler
            };

            console.log('✓ Map data updated successfully with icons');

        } catch (error) {
            console.error('✗ Error updating map data:', error);
            setError('Failed to update map display');
        } finally {
            isUpdatingMapRef.current = false;
        }
    }, [loadIcons]);

    // Store updateMapData in ref for loadCampData to use
    useEffect(() => {
        functionsRef.current.updateMapData = updateMapData;
    }, [updateMapData]);

    const changeMapStyle = useCallback((newStyle) => {
        if (!map.current || !mapStyles[newStyle] || isChangingStyleRef.current) {
            return;
        }

        console.log(`Changing map style to: ${newStyle}`);
        setMapStyle(newStyle);
        isChangingStyleRef.current = true;

        if (styleDataHandlerRef.current) {
            map.current.off('styledata', styleDataHandlerRef.current);
            styleDataHandlerRef.current = null;
        }

        let restored = false;
        const restoreData = () => {
            if (restored) return;
            restored = true;

            console.log('Restoring data after style change...');

            if (currentCampDataRef.current && map.current && map.current.loaded()) {
                // Add longer delay to ensure new style is fully loaded
                setTimeout(async () => {
                    console.log('Restoring camp data after style change...');
                    await updateMapData(currentCampDataRef.current, currentUserIdRef.current);

                    if (userLocation) {
                        addUserLocationToMap(userLocation);
                    }

                    isChangingStyleRef.current = false;
                }, 1000); // Increased delay for style changes
            } else {
                isChangingStyleRef.current = false;
            }
        };

        const styleDataHandler = () => {
            console.log('Style data loaded, waiting before restore...');
            // Add delay to ensure style is fully applied
            setTimeout(restoreData, 500);
        };

        map.current.once('styledata', styleDataHandler);
        styleDataHandlerRef.current = styleDataHandler;

        // Multiple fallback timeouts for different scenarios
        setTimeout(() => {
            if (!restored) {
                console.log('Style change fallback timeout (2s)');
                restoreData();
            }
        }, 2000);

        setTimeout(() => {
            if (!restored) {
                console.log('Style change fallback timeout (4s)');
                restoreData();
            }
        }, 4000);

        map.current.setStyle(mapStyles[newStyle].style);
    }, [mapStyles, updateMapData, userLocation, addUserLocationToMap]);

    const handleFilterChange = useCallback((filterName, value) => {
        setSelectedFilters(prevFilters => ({
            ...prevFilters,
            [filterName]: value
        }));
    }, []);

    const handleNearMeToggle = useCallback(() => {
        if (!nearMe) {
            // Turning on Near Me - get current location if we don't have it
            if (!userLocation) {
                setIsGettingLocation(true);
                getCurrentLocation();
            }
        }
        setNearMe(!nearMe);
    }, [nearMe, userLocation, getCurrentLocation]);

    // Make functions globally available
    useEffect(() => {
        window.getCurrentMapState = getCurrentMapState;
        window.reactRouterNavigate = navigate;

        return () => {
            delete window.getCurrentMapState;
            delete window.reactRouterNavigate;
        };
    }, [getCurrentMapState, navigate]);

    // ENHANCED: Main initialization effect with mobile icon loading fixes
    useEffect(() => {
        if (map.current || isInitializedRef.current) return;

        console.log('🗺️ Initializing map...');
        isInitializedRef.current = true;

        const initializeMap = async () => {
            if (!mapContainer.current) {
                setError('Map container not available');
                setLoadingWithTimeout(false);
                return;
            }

            if (!mapboxgl) {
                setError('Mapbox GL JS library not loaded');
                setLoadingWithTimeout(false);
                return;
            }

            try {
                const { mapboxToken: token, initialCenter: center, initialZoom: zoom } = propsRef.current;

                // ENHANCED: Better URL parameter handling for map state restoration
                let initialMapState = null;
                let shouldGetLocation = true; // Flag to determine if we should get user location
                let isMobileRefresh = false; // Flag for mobile refresh

                // Check for return parameters (from details page) - FIXED coordinate order
                const returnLat = searchParams.get('returnLat') || searchParams.get('lat');
                const returnLng = searchParams.get('returnLng') || searchParams.get('lng');
                const returnZoom = searchParams.get('returnZoom') || searchParams.get('zoom');
                const returnFrom = searchParams.get('returnFrom') || searchParams.get('from');
                const mobileRefresh = searchParams.get('mobileRefresh'); // Mobile refresh flag

                if (mobileRefresh) {
                    isMobileRefresh = true;
                    console.log('📱 Mobile refresh detected - will force icon reload');
                }

                if (returnLat && returnLng) {
                    const lat = parseFloat(returnLat);
                    const lng = parseFloat(returnLng);
                    const zoomVal = returnZoom ? parseFloat(returnZoom) : zoom;

                    // Validate coordinates
                    if (!isNaN(lat) && !isNaN(lng) && !isNaN(zoomVal) &&
                        Math.abs(lat) <= 90 && Math.abs(lng) <= 180 &&
                        zoomVal >= 0 && zoomVal <= 22) {

                        initialMapState = {
                            center: [lng, lat], // CRITICAL FIX: Mapbox uses [lng, lat] order!
                            zoom: zoomVal
                        };
                        shouldGetLocation = false; // Don't get location if we're restoring from URL

                        console.log('✅ Restoring map state:', {
                            lat: lat,
                            lng: lng,
                            zoom: zoomVal,
                            from: returnFrom || 'unknown',
                            mobile: isMobileRefresh
                        });
                    } else {
                        console.warn('❌ Invalid coordinates in URL:', { lat, lng, zoom: zoomVal });
                    }
                }

                // NEW: Auto-detect user location for initial map centering
                if (shouldGetLocation && navigator.geolocation) {
                    console.log('🔍 Attempting to get user location for initial map center...');
                    try {
                        const position = await new Promise((resolve, reject) => {
                            navigator.geolocation.getCurrentPosition(
                                resolve,
                                reject,
                                {
                                    enableHighAccuracy: true,
                                    timeout: 5000, // Shorter timeout for initial load
                                    maximumAge: 300000 // 5 minutes cache
                                }
                            );
                        });

                        const { latitude, longitude } = position.coords;

                        // Validate coordinates
                        if (latitude && longitude &&
                            !isNaN(latitude) && !isNaN(longitude) &&
                            Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180) {

                            initialMapState = {
                                center: [longitude, latitude], // Mapbox order: [lng, lat]
                                zoom: 8 // Zoom level for ~100 mile view (was 12)
                            };

                            // Store location for later use
                            const locationData = { latitude, longitude, accuracy: position.coords.accuracy };
                            setUserLocation(locationData);

                            console.log('✅ Using user location for initial map center:', {
                                lat: latitude,
                                lng: longitude,
                                accuracy: position.coords.accuracy,
                                zoom: 8
                            });
                        }
                    } catch (locationError) {
                        console.log('📍 Could not get user location for initial center:', locationError.message);
                        // Will fall back to default center
                    }
                } else if (!shouldGetLocation) {
                    console.log('📍 Skipping location detection - using URL state');
                } else {
                    console.log('📍 Geolocation not available - using default center');
                }

                map.current = new mapboxgl.Map({
                    container: mapContainer.current,
                    style: mapStyles[mapStyle].style,
                    center: initialMapState ? initialMapState.center : center,
                    zoom: initialMapState ? initialMapState.zoom : zoom,
                    accessToken: token,
                    attributionControl: false
                });

                popup.current = new mapboxgl.Popup({ offset: 15 });

                // Add controls
                map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
                map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');
                map.current.addControl(new mapboxgl.AttributionControl({
                    compact: true,
                    customAttribution: '© Open Trail Guide Contributors'
                }), 'bottom-right');

                // Add CSS to position controls below the orange header
                if (!document.getElementById('mapbox-control-styles')) {
                    const style = document.createElement('style');
                    style.id = 'mapbox-control-styles';
                    style.textContent = `
                        .mapboxgl-ctrl-top-right {
                            top: 80px !important; /* Push controls below orange header */
                        }
                        .mapboxgl-ctrl-top-left {
                            top: 80px !important; /* In case any controls are positioned top-left */
                        }
                    `;
                    document.head.appendChild(style);
                }

                // Handle successful load
                map.current.once('load', () => {
                    console.log('✅ Map loaded successfully at:', {
                        center: map.current.getCenter(),
                        zoom: map.current.getZoom()
                    });

                    // MOBILE FIX: Force icon reload on mobile refresh
                    if (isMobileRefresh) {
                        console.log('📱 Mobile refresh - forcing icon reload after map load');
                        setTimeout(async () => {
                            await loadIcons();
                            // Load data after icons are ready
                            setTimeout(() => {
                                loadCampData();
                            }, 500);
                        }, 1000);
                    } else {
                        // Normal data loading
                        loadCampData();
                    }

                    // Add user location marker if we detected location during initialization
                    if (userLocation && shouldGetLocation) {
                        console.log('📍 Adding user location marker from initial detection');
                        addUserLocationToMap(userLocation);
                    }

                    // Add map click handler to capture selected location
                    map.current.on('click', (e) => {
                        // Only capture location if clicking on empty space (not on markers/clusters)
                        const features = map.current.queryRenderedFeatures(e.point);
                        const campFeatures = features.filter(f => 
                            f.layer && (f.layer.id === 'camp-points' || f.layer.id === 'camp-clusters')
                        );
                        
                        // If not clicking on camp markers, store the clicked location
                        if (campFeatures.length === 0) {
                            const clickedLocation = {
                                lat: e.lngLat.lat,
                                lng: e.lngLat.lng
                            };
                            
                            setSelectedLocation(clickedLocation);
                            addSelectedLocationToMap(clickedLocation);
                            console.log('📍 Selected location from map click:', clickedLocation);
                        }
                    });

                    // Clean URL parameters if they were used for restoration
                    if (returnLat && returnLng) {
                        setTimeout(() => {
                            // Clean URL including mobile refresh flag
                            const cleanUrl = location.pathname;
                            console.log('🧹 Cleaning URL params, new URL:', cleanUrl);

                            // Use window.history.replaceState instead of navigate
                            window.history.replaceState(null, '', cleanUrl);
                        }, 1000);
                    }

                    updateScale();
                });

                // Handle errors
                map.current.on('error', (e) => {
                    console.error('Map error:', e);
                    setError('Map failed to load - please refresh the page');
                    setLoadingWithTimeout(false);
                });

                // Scale updates
                let scaleTimeout;
                const debouncedScaleUpdate = () => {
                    clearTimeout(scaleTimeout);
                    scaleTimeout = setTimeout(updateScale, 100);
                };

                map.current.on('moveend', debouncedScaleUpdate);
                map.current.on('zoomend', debouncedScaleUpdate);

            } catch (error) {
                console.error('Map initialization error:', error);
                setError(`Failed to initialize map: ${error.message}`);
                setLoadingWithTimeout(false);
            }
        };

        setTimeout(initializeMap, 100);

        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }

            if (map.current) {
                map.current.remove();
                map.current = null;
            }

            isInitializedRef.current = false;
            dataLoadedRef.current = false;
        };
    }, [
        mapStyles,
        mapStyle,
        searchParams
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Note: Functions like navigate, setLoadingWithTimeout, updateScale, addUserLocationToMap, 
        // addSelectedLocationToMap, loadIcons are intentionally excluded to prevent initialization loop.
        // They are stable functions that don't need to trigger re-initialization.
    ]);

    // FIXED: Handle filter changes with proper dependency
    useEffect(() => {
        if (!map.current || !map.current.loaded() || !dataLoadedRef.current) return;

        const timeoutId = setTimeout(() => {
            console.log('Filter changed, reloading data:', selectedFilters, 'nearMe:', nearMe);
            loadCampData();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [selectedFilters, nearMe, loadCampData]);

    // Handle user location
    useEffect(() => {
        if (userLocation && map.current && map.current.loaded()) {
            addUserLocationToMap(userLocation);
        }
    }, [userLocation, addUserLocationToMap]);

    // Handle selected location marker
    useEffect(() => {
        if (selectedLocation && map.current && map.current.loaded()) {
            addSelectedLocationToMap(selectedLocation);
        }
    }, [selectedLocation, addSelectedLocationToMap]);

    // Error boundary for critical errors
    if (error && !campData && !isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-red-50">
                <div className="text-center p-6 rounded-lg shadow-md bg-white max-w-md">
                    <h2 className="text-lg font-semibold text-red-700 mb-2">Map Error</h2>
                    <p className="text-red-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                    >
                        Reload Page
                    </button>
                </div>
            </div>
        );
    }

    const currentStyleDescription = mapStyles[mapStyle]?.description || '';

    return (
        <div className="camp-map-container relative w-full h-screen">
            {/* Header */}
            <div className="bg-orange-600 text-white px-6 py-4 relative z-10">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded">
                            <Tent className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">OTG Camp</h1>
                            <p className="text-sm opacity-90">OFF THE GRID</p>
                        </div>
                    </div>
                    
                    {/* Navigation Icons - Centered on desktop, left of Pro on mobile */}
                    <div className="hidden md:flex space-x-2 absolute left-1/2 transform -translate-x-1/2">
                        <button
                            onClick={() => navigate('/events')}
                            className="bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors duration-200 w-12 h-12 flex items-center justify-center"
                            title="Go to Events Home"
                        >
                            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                            </svg>
                        </button>

                        <button
                            onClick={getCurrentLocation}
                            disabled={isGettingLocation}
                            className={`
                                bg-blue-600 text-white rounded-lg shadow-lg p-3 w-12 h-12
                                flex items-center justify-center transition-colors duration-200
                                ${isGettingLocation ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}
                            `}
                            title="Find My Location"
                        >
                            {isGettingLocation ? (
                                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                            ) : (
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd"
                                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                          clipRule="evenodd"/>
                                </svg>
                            )}
                        </button>

                        {/* Add Location Button - Only show if user has permissions */}
                        {(isAdmin || isTA ) && (
                            <button
                                onClick={handleAddLocation}
                                className="bg-green-600 text-white rounded-lg shadow-lg p-3 w-12 h-12 flex items-center justify-center transition-colors duration-200 hover:bg-green-700"
                                title={selectedLocation ? "Add Location at Selected Point" : "Add Location at Map Center"}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                                </svg>
                            </button>
                        )}

                        {/* Near Me Button */}
                        <button
                            onClick={handleNearMeToggle}
                            disabled={isGettingLocation}
                            className={`
                                rounded-lg shadow-lg p-3 w-12 h-12 text-white
                                flex items-center justify-center transition-colors duration-200
                                ${nearMe 
                                    ? 'bg-purple-600 hover:bg-purple-700' 
                                    : 'bg-gray-600 hover:bg-gray-700'
                                }
                                ${isGettingLocation ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            title={
                                isGettingLocation 
                                    ? "Getting your location..." 
                                    : nearMe 
                                        ? "Near Me filter active - Click to disable" 
                                        : "Show camps near your location"
                            }
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                            </svg>
                        </button>
                        
                        {/* Clear Selected Location Button */}
                        {selectedLocation && (isAdmin || isTA) && (
                            <button
                                onClick={() => {
                                    setSelectedLocation(null);
                                    if (selectedLocationMarker.current) {
                                        selectedLocationMarker.current.remove();
                                        selectedLocationMarker.current = null;
                                    }
                                    console.log('🗑️ Cleared selected location');
                                }}
                                className="bg-orange-700 text-white rounded-lg shadow-lg p-3 w-12 h-12 flex items-center justify-center transition-colors duration-200 hover:bg-orange-800"
                                title="Clear Selected Location"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {/* Mobile Navigation Icons - Smaller, left of Pro */}
                        <div className="flex md:hidden space-x-1">
                            <button
                                onClick={() => navigate('/events')}
                                className="bg-white rounded-lg shadow-lg p-2 hover:bg-gray-50 transition-colors duration-200 w-8 h-8 flex items-center justify-center"
                                title="Go to Events Home"
                            >
                                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                                </svg>
                            </button>

                            <button
                                onClick={getCurrentLocation}
                                disabled={isGettingLocation}
                                className={`
                                    bg-blue-600 text-white rounded-lg shadow-lg p-2 w-8 h-8
                                    flex items-center justify-center transition-colors duration-200
                                    ${isGettingLocation ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}
                                `}
                                title="Find My Location"
                            >
                                {isGettingLocation ? (
                                    <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
                                ) : (
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd"
                                              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                              clipRule="evenodd"/>
                                    </svg>
                                )}
                            </button>

                            {/* Add Location Button - Only show if user has permissions */}
                            {(isAdmin || isTA ) && (
                                <button
                                    onClick={handleAddLocation}
                                    className="bg-green-600 text-white rounded-lg shadow-lg p-2 w-8 h-8 flex items-center justify-center transition-colors duration-200 hover:bg-green-700"
                                    title={selectedLocation ? "Add Location at Selected Point" : "Add Location at Map Center"}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                                    </svg>
                                </button>
                            )}

                            {/* Near Me Button - Mobile */}
                            <button
                                onClick={handleNearMeToggle}
                                disabled={isGettingLocation}
                                className={`
                                    rounded-lg shadow-lg p-2 w-8 h-8 text-white
                                    flex items-center justify-center transition-colors duration-200
                                    ${nearMe 
                                        ? 'bg-purple-600 hover:bg-purple-700' 
                                        : 'bg-gray-600 hover:bg-gray-700'
                                    }
                                    ${isGettingLocation ? 'opacity-50 cursor-not-allowed' : ''}
                                `}
                                title={
                                    isGettingLocation 
                                        ? "Getting your location..." 
                                        : nearMe 
                                            ? "Near Me filter active - Click to disable" 
                                            : "Show camps near your location"
                                }
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                                </svg>
                            </button>
                            
                            {/* Clear Selected Location Button */}
                            {selectedLocation && (isAdmin || isTA) && (
                                <button
                                    onClick={() => {
                                        setSelectedLocation(null);
                                        if (selectedLocationMarker.current) {
                                            selectedLocationMarker.current.remove();
                                            selectedLocationMarker.current = null;
                                        }
                                        console.log('🗑️ Cleared selected location');
                                    }}
                                    className="bg-orange-700 text-white rounded-lg shadow-lg p-2 w-8 h-8 flex items-center justify-center transition-colors duration-200 hover:bg-orange-800"
                                    title="Clear Selected Location"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                        
                        <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                    </div>
                </div>
            </div>
            {isLoading && (
                <div className="absolute inset-0 z-50 bg-white bg-opacity-90 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-700 font-medium">Loading map data...</p>
                        {error && (
                            <p className="text-red-500 mt-2 text-sm">{error}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Map Container */}
            <div
                ref={mapContainer}
                className="w-full h-full"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                }}
                data-testid="mapbox-map-container"
            />

            {/* Filter Controls - Positioned below header */}
            <div className="absolute top-24 left-2 z-10 flex flex-col space-y-2">
                {/* Filter Controls */}
                <div className="bg-white p-2 rounded-lg shadow-md flex flex-col space-y-2 w-fit">
                    <label className="text-sm text-gray-700 flex items-center space-x-2 w-fit">
                        <select
                            value={selectedFilters.type}
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                            className="p-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
                            disabled={isLoading}
                        >
                            <option value="all">All Types</option>
                            <option value="campground">🏕️ Campground</option>
                            <option value="dispersed">🌲 Dispersed</option>
                            <option value="rv park">🚐 RV Park</option>
                            <option value="group camp">👥 Group Camp</option>
                            <option value="stealth camp">🥷 Stealth Camp</option>
                            <option value="no camping">🚫 No Camping</option>
                        </select>
                    </label>
                </div>

                {/* Map Style Selector */}
                <div className="bg-white p-2 rounded-lg shadow-md flex items-center space-x-2 w-fit">
                    <select
                        id="mapStyle"
                        value={mapStyle}
                        onChange={(e) => changeMapStyle(e.target.value)}
                        className="p-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                        title={currentStyleDescription}
                    >
                        {Object.entries(mapStyles).map(([key, style]) => (
                            <option key={key} value={key}>
                                {style.name}
                            </option>
                        ))}
                    </select>
                </div>

                {locationError && (
                    <div
                        className="bg-red-100 border border-red-400 text-red-700 px-2 py-1 rounded relative text-xs max-w-48"
                        role="alert">
                        {locationError}
                    </div>
                )}
            </div>

            {/* Custom Scale Bar */}
            <div className="absolute bottom-4 left-4 z-10 bg-white p-2 rounded-lg shadow-md">
                <div className="text-xs text-gray-700 mb-1">Scale: {scaleMiles.value} {scaleMiles.unit}</div>
                <div className="bg-gray-300 h-1 rounded-full" style={{width: `${scaleWidth}px`}}></div>
            </div>

            {/* Map Legend */}
            <div className="absolute bottom-4 right-4 z-10 bg-white rounded-lg shadow-md text-xs sm:text-sm">
                <button
                    onClick={() => setLegendExpanded(!legendExpanded)}
                    className="w-full p-3 flex items-center justify-between hover:bg-gray-50 rounded-lg transition-colors"
                    title={legendExpanded ? "Collapse Legend" : "Expand Legend"}
                >
                    <h3 className="font-semibold text-gray-800">Map Legend</h3>
                    <svg
                        className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${legendExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                    </svg>
                </button>

                {legendExpanded && (
                    <div className="px-3 pb-3 space-y-1 sm:space-y-2 border-t pt-2 max-w-[180px] sm:max-w-xs">
                        <div className="flex items-center">
                            <div
                                className="w-6 h-6 mr-2 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">🏕️
                            </div>
                            <span className="text-sm text-gray-700">Campground</span>
                        </div>
                        <div className="flex items-center">
                            <div
                                className="w-6 h-6 mr-2 bg-yellow-500 rounded-full flex items-center justify-center text-white text-xs">🌲</div>
                            <span className="text-sm text-gray-700">Dispersed</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-6 h-6 mr-2 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">🚐</div>
                            <span className="text-sm text-gray-700">RV Park</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-6 h-6 mr-2 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs">👥</div>
                            <span className="text-sm text-gray-700">Group Camp</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-6 h-6 mr-2 bg-gray-700 rounded-full flex items-center justify-center text-white text-xs">🥷</div>
                            <span className="text-sm text-gray-700">Stealth Camp</span>
                        </div>
                        <div className="flex items-center">
                            <div className="w-6 h-6 mr-2 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">🚫</div>
                            <span className="text-sm text-gray-700">No Camping</span>
                        </div>
                        {userLocation && (
                            <div className="flex items-center border-t pt-2 mt-2">
                                <div className="w-6 h-6 mr-2 bg-blue-600 rounded-full flex items-center justify-center">
                                    <div className="w-3 h-3 bg-white rounded-full"></div>
                                </div>
                                <span className="text-sm text-gray-700">Your Location</span>
                            </div>
                        )}
                        {selectedLocation && (
                            <div className="flex items-center border-t pt-2 mt-2">
                                <div className="w-6 h-6 mr-2 bg-orange-500 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                </div>
                                <span className="text-sm text-gray-700">Selected Location</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CampMap;